import {flavorDescriptions, colors, strokeWidth} from '../constants';

import {
  hasCoords,
  createPoint,
  buildGeometryInstance,
  isSameCoordinatePair,
  getLayerForFlavoredEntityType,
  handleAddCorrectnessIcon
} from './entities.utils';

const polygon = {
  /**
   * @example
   * const entity = {
   *   type: 'polygon',
   *   elements: {
   *     points: [
   *       [0, 0],
   *       [0, 2],
   *       [2, 2],
   *       [2, 0],
   *     ]
   *   },
   *   flavor: 'studentGuess'
   * }
   */
  create({board, entity, onClick}) {
    const {
      elements: {points},
      flavor
    } = entity;

    const polygonPoints = points.map((coords) => createPoint({board, coords, flavor}));

    const polygon = board.create('polygon', polygonPoints, {
      borders: {
        strokeWidth,
        highlightStrokeWidth: strokeWidth,
        strokeColor: flavorDescriptions[flavor].color,
        highlightStrokeColor: flavorDescriptions[flavor].color,
        layer: getLayerForFlavoredEntityType('line', flavor),
        tabindex: null
      },
      highlightFillColor: 'transparent',
      fillColor: 'transparent',
      tabindex: null
    });

    const correctnessIcon = handleAddCorrectnessIcon({board, flavor, coords: points[0]});

    return buildGeometryInstance({
      board,
      entity,
      geometryElement: polygon,
      constituentElements: [...polygonPoints, polygon, ...polygon.borders],
      onClick,
      correctnessIcon
    });
  },
  createPartial({board, partialInstance, coords, flavor}) {
    if (!partialInstance) {
      return {
        entity: {
          type: 'polygon',
          elements: {
            points: []
          },
          flavor
        },
        geometryElements: [],
        done: false,
        remove() {
          return null;
        }
      };
    }

    const {
      entity: {elements},
      geometryElements
    } = partialInstance;

    if (elements.points[0] && elements.points.length >= 3) {
      const [startX, startY] = elements.points[0];
      const [currX, currY] = coords;
      if (startX === currX && startY === currY) {
        return {...partialInstance, done: true};
      }
    }

    if (hasCoords(elements, coords)) {
      return null;
    }

    const nextPoint = createPoint({board, coords, flavor});

    const nextGeometryElements = [...geometryElements, nextPoint];

    const lastPointCoords = elements.points[elements.points.length - 1];

    if (lastPointCoords) {
      const tempLine = board.create('line', [lastPointCoords, coords], {
        strokeWidth,
        highlightStrokeWidth: strokeWidth,
        straightFirst: false,
        straightLast: false,
        strokeColor: flavorDescriptions[flavor].color,
        tabindex: null
      });
      nextGeometryElements.push(tempLine);
    }

    return {
      entity: {
        type: 'polygon',
        elements: {
          points: [...elements.points, coords]
        },
        flavor
      },
      geometryElements: nextGeometryElements,
      done: false,
      instructions: 'Place all vertices then close the shape by selecting the first point.',
      handleCursorMove(cursorCoords) {
        if (board.elementsByName.previewPolygonLine) {
          board.removeAncestors(board.elementsByName.previewPolygonLine);
        }

        if (!coords || !cursorCoords || isSameCoordinatePair(coords, cursorCoords)) {
          return;
        }

        board.create('line', [coords, cursorCoords], {
          name: 'previewPolygonLine',
          strokeWidth,
          highlightStrokeWidth: strokeWidth,
          strokeColor: colors.preview,
          highlightStrokeColor: colors.preview,
          dash: 3,
          straightFirst: false,
          straightLast: false,
          tabindex: null
        });
      },
      remove() {
        nextGeometryElements.forEach((element) => board.removeAncestors(element));
        return null;
      }
    };
  }
};

export default polygon;
